import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePickerInput } from '@/components/ui/date-picker-input';
import { AlertTriangle, Ban, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/utils';

interface CancelDealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  dealName: string;
  onConfirm: (data: {
    reason: string;
    cancelledAt: Date;
    cancelPayments: boolean;
  }) => void;
  isLoading?: boolean;
}

const CANCELLATION_REASONS = [
  { value: 'client_decision', label: 'Desistência do cliente' },
  { value: 'financial_issues', label: 'Problemas financeiros' },
  { value: 'service_dissatisfaction', label: 'Insatisfação com serviço' },
  { value: 'changed_plans', label: 'Mudança de planos' },
  { value: 'competitor', label: 'Foi para concorrente' },
  { value: 'no_response', label: 'Sem resposta/Ghosting' },
  { value: 'other', label: 'Outro' },
];

export default function CancelDealModal({
  open,
  onOpenChange,
  dealId,
  dealName,
  onConfirm,
  isLoading = false
}: CancelDealModalProps) {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState('');
  const [cancelledAt, setCancelledAt] = useState<Date>(new Date());
  const [cancelPayments, setCancelPayments] = useState(true);
  const [pendingPayments, setPendingPayments] = useState<{ count: number; total: number }>({ count: 0, total: 0 });
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Fetch pending payments info when modal opens
  useEffect(() => {
    if (open && dealId) {
      fetchPendingPayments();
    }
  }, [open, dealId]);

  const fetchPendingPayments = async () => {
    setLoadingPayments(true);
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('id, amount, balance, status')
        .eq('deal_id', dealId)
        .in('status', ['pending', 'overdue', 'partial', 'partially_paid']);

      if (error) throw error;

      const count = data?.length || 0;
      const total = data?.reduce((sum, p) => sum + (p.balance || p.amount || 0), 0) || 0;
      
      setPendingPayments({ count, total });
    } catch (error) {
      console.error('Error fetching pending payments:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleConfirm = () => {
    const reason = selectedReason === 'other' 
      ? customReason 
      : CANCELLATION_REASONS.find(r => r.value === selectedReason)?.label || selectedReason;

    onConfirm({
      reason,
      cancelledAt,
      cancelPayments
    });
  };

  const isValid = selectedReason && (selectedReason !== 'other' || customReason.trim());

  const handleClose = () => {
    if (!isLoading) {
      setSelectedReason('');
      setCustomReason('');
      setCancelledAt(new Date());
      setCancelPayments(true);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-destructive/10">
              <Ban className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <DialogTitle className="text-lg">Cancelar Contrato</DialogTitle>
              <DialogDescription className="text-sm">
                {dealName}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Cancellation Reason */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Motivo do Cancelamento *</Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione o motivo..." />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedReason === 'other' && (
              <Textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Descreva o motivo do cancelamento..."
                className="mt-2"
                rows={3}
              />
            )}
          </div>

          {/* Cancellation Date */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Data do Cancelamento *</Label>
            <DatePickerInput
              value={cancelledAt}
              onChange={(date) => date && setCancelledAt(date)}
            />
          </div>

          {/* Cancel Payments Option */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="cancel-payments"
                checked={cancelPayments}
                onCheckedChange={(checked) => setCancelPayments(checked === true)}
              />
              <div className="space-y-1">
                <Label htmlFor="cancel-payments" className="text-sm font-medium cursor-pointer">
                  Cancelar pagamentos futuros pendentes
                </Label>
                {!loadingPayments && pendingPayments.count > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {pendingPayments.count} pagamento{pendingPayments.count > 1 ? 's' : ''} pendente{pendingPayments.count > 1 ? 's' : ''} ({formatCurrency(pendingPayments.total)})
                  </p>
                )}
                {!loadingPayments && pendingPayments.count === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum pagamento pendente encontrado
                  </p>
                )}
              </div>
            </div>

            {cancelPayments && pendingPayments.count > 0 && (
              <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
                  {pendingPayments.count} pagamento{pendingPayments.count > 1 ? 's' : ''} ({formatCurrency(pendingPayments.total)}) ser{pendingPayments.count > 1 ? 'ão' : 'á'} marcado{pendingPayments.count > 1 ? 's' : ''} como cancelado{pendingPayments.count > 1 ? 's' : ''}.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isLoading}
          >
            Voltar
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Cancelando...
              </>
            ) : (
              'Confirmar Cancelamento'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
